import React, { useEffect, useState } from "react";
import { Button, TextField } from "@mui/material";
import moment from "moment";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import NoData from "../../loaders/NoData";
import SkeletonLoader from "../../loaders/Skeleton";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RatingInput from "./RatingInput";
import { STATUS, byCategory, scoreTone, compareByCategory, gapLabel, gapTone } from "./helpers";
import "./Performance.scss";

const MyReview = () => {

    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        const resp = await fetchData({ url: URLS.reviewMine, method: "get" });
        setLoading(false);
        if (resp?.success === false) {
            toast.error(resp?.message || "Could not load your reviews");
            return;
        }
        setReviews(resp.data || []);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const open = (review) => {
        setOpenId(review._id);
        setDraft({
            items: review.items.map((i) => ({
                selfRating: i.selfRating || 0,
                selfComment: i.selfComment || "",
            })),
            selfComment: review.selfComment || "",
        });
    };

    const save = async (review, submit) => {
        if (busy) return;
        setBusy(true);
        const resp = await fetchData({
            url: `${URLS.reviewMine}/${review._id}`,
            method: "put",
            body: { ...draft, submit },
        });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not save");
            return;
        }
        toast.success(resp?.message || "Saved");
        if (submit) { setOpenId(null); setDraft(null); }
        load();
    };

    if (loading) return <SkeletonLoader />;

    if (!reviews.length) {
        return <NoData
            title="No reviews yet"
            message="When a review cycle opens, your self assessment appears here." />;
    }

    return (
        <div className="perf-page">
            {reviews.map((review) => {
                const editing = openId === review._id && review.status === "pending"
                    && review.cycle?.status === "self";
                const meta = STATUS[review.status] || STATUS.pending;

                return (
                    <div className="wh-panel perf-review" key={review._id}>
                        <div className="perf-review-head">
                            <div>
                                <h6>{review.cycle?.name}</h6>
                                <span>
                                    {review.cycle?.startDate
                                        ? `${moment(review.cycle.startDate).format("DD MMM")} – ${moment(review.cycle.endDate).format("DD MMM YYYY")}`
                                        : "Quarterly review"}
                                    {review.managerName ? ` · assessed by ${review.managerName}` : ""}
                                </span>
                            </div>

                            <span className={`wh-pill ${meta.tone}`}>{meta.label}</span>

                            {review.selfScore > 0 && (
                                <span className={`wh-pill ${scoreTone(review.selfScore)} perf-score`}>
                                    You {review.selfScore}/5
                                </span>
                            )}

                            {review.status === "completed" && (
                                <span className={`wh-pill ${scoreTone(review.managerScore)} perf-score`}>
                                    Manager {review.managerScore}/5
                                </span>
                            )}

                            {review.status === "pending" && review.cycle?.status === "self" && !editing && (
                                <Button variant="contained" onClick={() => open(review)}>
                                    Start
                                </Button>
                            )}
                        </div>

                        {(editing || review.status !== "pending") && (
                            <div className="perf-body">

                                {/* the whole point of the page: your number,
                                    their number, and the distance between */}
                                {review.selfScore > 0 && !editing && (
                                    <div className="perf-standing">
                                        <div className="perf-standing-scores">
                                            <div className="perf-standing-score">
                                                <span>You rated yourself</span>
                                                <b>{review.selfScore}<em>/5</em></b>
                                            </div>

                                            <div className={`perf-standing-score ${review.status === "completed" ? "" : "is-waiting"}`}>
                                                <span>{review.managerName || "Your manager"}</span>
                                                {review.status === "completed"
                                                    ? <b>{review.managerScore}<em>/5</em></b>
                                                    : <i>Not rated yet</i>}
                                            </div>

                                            {review.status === "completed" && (
                                                <div className={`perf-standing-gap ${gapTone(review.managerScore - review.selfScore)}`}>
                                                    <span>Where you stand</span>
                                                    <b>{gapLabel(Math.round((review.managerScore - review.selfScore) * 100) / 100)}</b>
                                                </div>
                                            )}
                                        </div>

                                        {review.status === "completed" && (
                                            <>
                                                <div className="perf-compare-legend">
                                                    <span className="perf-key perf-key--self">You</span>
                                                    <span className="perf-key perf-key--mgr">{review.managerName || "Manager"}</span>
                                                </div>

                                                <div className="perf-compare">
                                                    {compareByCategory(review.items).map((group) => (
                                                        <div className="perf-compare-row" key={group.name}>
                                                            <span className="perf-compare-label">
                                                                {group.name}
                                                                {group.weight > 0 && <em>{group.weight}%</em>}
                                                            </span>

                                                            <span className="perf-compare-bars">
                                                                <span className="perf-compare-track">
                                                                    <span className="perf-compare-bar perf-compare-bar--self"
                                                                        style={{ width: `${(group.self / 5) * 100}%` }} />
                                                                </span>
                                                                <span className="perf-compare-track">
                                                                    <span className="perf-compare-bar perf-compare-bar--mgr"
                                                                        style={{ width: `${(group.manager / 5) * 100}%` }} />
                                                                </span>
                                                            </span>

                                                            <span className="perf-compare-nums">
                                                                <b>{group.self || "--"}</b>
                                                                <b>{group.manager || "--"}</b>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {byCategory(review.items).map((group) => (
                                    <div className="perf-group" key={group.name}>
                                        <div className="perf-group-head">
                                            <h6>{group.name}</h6>
                                            {group.weight > 0 && <span className="wh-pill wh-pill-quiet">{group.weight}%</span>}
                                        </div>

                                        {group.rows.map((row) => (
                                            <div className="perf-kra" key={row.index}>
                                                <div className="perf-kra-text">
                                                    <b>{row.kra}</b>
                                                    {row.metric && <em>{row.metric}</em>}
                                                </div>

                                                <div className="perf-kra-side">
                                                    <span className="perf-kra-label">Your rating</span>
                                                    <RatingInput
                                                        label={`self-${row.index}`}
                                                        value={editing ? draft.items[row.index].selfRating : row.selfRating}
                                                        readOnly={!editing}
                                                        onChange={(next) => {
                                                            const items = [...draft.items];
                                                            items[row.index] = { ...items[row.index], selfRating: next };
                                                            setDraft({ ...draft, items });
                                                        }}
                                                    />
                                                </div>

                                                <div className="perf-kra-note">
                                                    {editing
                                                        ? <TextField
                                                            fullWidth multiline minRows={2} size="small"
                                                            label="Your comment"
                                                            placeholder="What you did, and how it went"
                                                            value={draft.items[row.index].selfComment}
                                                            onChange={(e) => {
                                                                const items = [...draft.items];
                                                                items[row.index] = { ...items[row.index], selfComment: e.target.value };
                                                                setDraft({ ...draft, items });
                                                            }} />
                                                        : <p className="perf-said">
                                                            {row.selfComment || <em>No comment</em>}
                                                        </p>}
                                                </div>

                                                {review.status === "completed" && (
                                                    <div className="perf-kra-manager">
                                                        <span className="perf-kra-label">
                                                            {review.managerName || "Manager"}
                                                        </span>
                                                        <RatingInput value={row.managerRating} readOnly
                                                            label={`mgr-${row.index}`} />
                                                        <p className="perf-said">
                                                            {row.managerComment || <em>No comment</em>}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                <div className="perf-overall">
                                    {editing
                                        ? <TextField
                                            fullWidth multiline minRows={3} size="small"
                                            label="Anything else for your manager"
                                            placeholder="Highlights, challenges, what you would like next"
                                            value={draft.selfComment}
                                            onChange={(e) => setDraft({ ...draft, selfComment: e.target.value })} />
                                        : (
                                            <>
                                                {review.selfComment && (
                                                    <div className="perf-quote">
                                                        <span>What you said</span>
                                                        <p>{review.selfComment}</p>
                                                    </div>
                                                )}
                                                {review.managerComment && (
                                                    <div className="perf-quote perf-quote--manager">
                                                        <span>{review.managerName || "Your manager"}</span>
                                                        <p>{review.managerComment}</p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                </div>

                                {editing && (
                                    <div className="perf-actions">
                                        <Button variant="outlined" startIcon={<SaveRoundedIcon />}
                                            disabled={busy} onClick={() => save(review, false)}>
                                            Save draft
                                        </Button>
                                        <Button variant="contained" startIcon={<SendRoundedIcon />}
                                            disabled={busy} onClick={() => save(review, true)}>
                                            Submit to manager
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default MyReview;
